// vg-binding: P7.D-11
//
// apps/vendor/src/features/products/InactiveCountdownChip.tsx
//
// Renders the countdown remaining for products in `inactive_countdown`
// state. Reads `ends_at` from the product row; shows hours/days remaining.

import { cn } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';

interface Props {
  ends_at: number | null;
}

type Tone = 'amber' | 'red';

function computeRemaining(
  endsAt: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): { label: string; tone: Tone } {
  const ms = endsAt - Date.now();
  if (ms <= 0) return { label: t('countdown.expired'), tone: 'red' };
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 24) {
    return { label: t('countdown.hoursRemaining', { hours }), tone: 'red' };
  }
  const days = Math.ceil(hours / 24);
  return {
    label: t('countdown.daysRemaining', { days }),
    tone: days <= 2 ? 'red' : 'amber',
  };
}

const TONE: Record<Tone, string> = {
  amber: 'bg-yellow-100 text-yellow-800',
  red: 'bg-red-100 text-red-800',
};

export function InactiveCountdownChip({ ends_at }: Props): JSX.Element | null {
  const { t } = useTranslation('products');
  if (typeof ends_at !== 'number') return null;
  const r = computeRemaining(ends_at, t);
  return (
    <span
      data-testid="inactive-countdown-chip"
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold',
        TONE[r.tone],
      )}
    >
      ⏳ {r.label}
    </span>
  );
}

export default InactiveCountdownChip;
